import { useMemo, useState } from 'react';
import { Lock, MessagesSquare, Plus, KeyRound, Radio, X, HardDrive, Flag } from 'lucide-react';
import { useStore, type Membership } from '@/relatos/store';
import { DEFAULT_RELAYS, isForumAdmin, MODERATOR_PUBKEY } from '@/relatos/kinds';
import { shortNpub } from './bits';
import { getBlossomServer, setBlossomServer } from '@/lib/blossom';
import { TimeAgo, displayName } from './bits';
import { bbcodeToText } from '@/lib/bbcode';

function BoardStats({ m }: { m: Membership }) {
  const { data, profiles } = useStore();
  const d = data[m.boardId];
  const threads = d?.posts.filter(p => p.payload.kind === 'thread') ?? [];
  const last = d?.posts.slice().sort((a, b) => b.ev.created_at - a.ev.created_at)[0];
  return (
    <>
      <td className="rl-num">{threads.length}</td>
      <td className="rl-num">{d?.posts.length ?? 0}</td>
      <td className="rl-last">
        {last ? (
          <>
            <TimeAgo ts={last.ev.created_at} /><br />
            by {displayName(last.ev.pubkey, profiles)}
            {last.payload.kind === 'thread' && last.payload.subject
              ? <><br />in <em>{bbcodeToText(last.payload.subject).slice(0, 40)}</em></>
              : null}
          </>
        ) : <span className="rl-small">No posts yet</span>}
      </td>
    </>
  );
}

function CreateBoard() {
  const { createBoard, signer } = useStore();
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const canMakePublic = !!signer && isForumAdmin(signer.pubkey);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const go = async () => {
    setBusy(true); setErr(null);
    try {
      const id = await createBoard(name.trim(), desc.trim(), isPublic);
      window.location.hash = `#/b/${id}`;
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    setBusy(false);
  };

  return (
    <>
      <div className="rl-cat"><span>Create a board</span><Plus size={14} aria-hidden /></div>
      <div className="rl-panel" style={{ borderTop: 'none', borderRadius: '0 0 3px 3px' }}>
        <div className="rl-field">
          <label htmlFor="cb-name">Board name</label>
          <input id="cb-name" className="rl-input" value={name} onChange={e => setName(e.target.value)} maxLength={80} />
        </div>
        <div className="rl-field">
          <label htmlFor="cb-desc">Description</label>
          <input id="cb-desc" className="rl-input" value={desc} onChange={e => setDesc(e.target.value)} maxLength={200} />
        </div>
        {canMakePublic && (
          <div className="rl-field" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input id="cb-public" type="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />
            <label htmlFor="cb-public" style={{ margin: 0 }}>
              Public board — anyone can read &amp; post, no invite needed
            </label>
          </div>
        )}
        <button className="rl-btn rl-btn-primary" disabled={busy || !name.trim()} onClick={go}>
          {busy ? 'Creating…' : 'Create board'}
        </button>
        {err && <p style={{ color: 'var(--rl-bad)', fontSize: 12 }}>{err}</p>}
        <p className="rl-small" style={{ marginTop: 8 }}>
          {isPublic
            ? 'Public boards publish their read key in the board definition — posts are still ciphertext on the relay, but anyone can decrypt them.'
            : 'A fresh epoch key is generated locally. The relay only ever sees ciphertext — name and description included.'}
        </p>
      </div>
    </>
  );
}

function PublicBoards() {
  const { publicBoards, boards, joinPublicBoard } = useStore();
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const list = useMemo(
    () => Object.values(publicBoards)
      .filter(b => !boards[b.boardId])
      .sort((a, b) => a.meta.name.localeCompare(b.meta.name)),
    [publicBoards, boards],
  );
  if (list.length === 0) return null;

  const open = async (boardId: string) => {
    setBusy(boardId); setErr(null);
    try {
      await joinPublicBoard(boardId);
      window.location.hash = `#/b/${boardId}`;
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    setBusy(null);
  };

  return (
    <>
      <div className="rl-cat"><span>Public boards — no invite needed</span><MessagesSquare size={13} aria-hidden /></div>
      <table className="rl-table">
        <tbody>
          {list.map((b, i) => (
            <tr key={b.boardId} className={i % 2 ? 'rl-row-b' : 'rl-row-a'}>
              <td className="rl-icon"><MessagesSquare size={17} aria-hidden /></td>
              <td>
                <div className="rl-board-name">
                  <a href={`#/b/${b.boardId}`} onClick={e => { e.preventDefault(); open(b.boardId); }}>
                    {b.meta.name}
                  </a>
                </div>
                <div className="rl-desc">{b.meta.description}</div>
              </td>
              <td className="rl-last" style={{ textAlign: 'right' }}>
                <button className="rl-btn rl-btn-sm" disabled={busy === b.boardId} onClick={() => open(b.boardId)}>
                  {busy === b.boardId ? 'Opening…' : 'Open'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {err && <p style={{ color: 'var(--rl-bad)', fontSize: 12 }}>{err}</p>}
    </>
  );
}

function JoinBoard() {
  const { joinWithInvite } = useStore();
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const go = async () => {
    setBusy(true); setErr(null);
    try {
      const id = await joinWithInvite(link.trim());
      window.location.hash = `#/b/${id}`;
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    setBusy(false);
  };

  return (
    <>
      <div className="rl-cat"><span>Redeem an invite</span><KeyRound size={14} aria-hidden /></div>
      <div className="rl-panel" style={{ borderTop: 'none', borderRadius: '0 0 3px 3px' }}>
        <div className="rl-field">
          <label htmlFor="jb-link">Invite link or token</label>
          <input id="jb-link" className="rl-input rl-mono" placeholder="…#/join/relatos1…" value={link} onChange={e => setLink(e.target.value)} />
        </div>
        <button className="rl-btn rl-btn-primary" disabled={busy || !link.trim()} onClick={go}>
          {busy ? 'Redeeming…' : 'Join board'}
        </button>
        {err && <p style={{ color: 'var(--rl-bad)', fontSize: 12 }}>{err}</p>}
        <p className="rl-small" style={{ marginTop: 8 }}>
          Invites are bound to your pubkey — a link meant for someone else will not open for you.
        </p>
      </div>
    </>
  );
}

function RelaySettings() {
  const { customRelays, addRelay, removeRelay, relayHealth } = useStore();
  const [url, setUrl] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const add = () => {
    setErr(null);
    try {
      addRelay(url);
      setUrl('');
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <>
      <div className="rl-cat"><span>Relays</span><Radio size={14} aria-hidden /></div>
      <div className="rl-panel" style={{ borderTop: 'none', borderRadius: '0 0 3px 3px' }}>
        {DEFAULT_RELAYS.map(r => (
          <div key={r} className="rl-actions" style={{ marginBottom: 4 }}>
            <span className="rl-mono rl-small">{r}</span>
            <span className="rl-pill">{relayHealth[r] ? 'up' : 'down'}</span>
          </div>
        ))}
        {customRelays.map(r => (
          <div key={r} className="rl-actions" style={{ marginBottom: 4 }}>
            <span className="rl-mono rl-small">{r}</span>
            <span className="rl-pill">{relayHealth[r] ? 'up' : 'down'}</span>
            <button className="rl-btn rl-btn-sm rl-btn-danger" onClick={() => removeRelay(r)}>
              <X size={11} aria-hidden style={{ verticalAlign: -1 }} /> Remove
            </button>
          </div>
        ))}
        <div className="rl-field" style={{ marginTop: 8 }}>
          <label htmlFor="rs-url">Add a default relay (applies to every board)</label>
          <input id="rs-url" className="rl-input rl-mono" placeholder="wss://relay.example.com"
                 value={url} onChange={e => setUrl(e.target.value)} />
        </div>
        <button className="rl-btn" disabled={!url.trim()} onClick={add}>Add relay</button>
        {err && <p style={{ color: 'var(--rl-bad)', fontSize: 12 }}>{err}</p>}
        <p className="rl-small" style={{ marginTop: 8 }}>
          Posts go to every relay at once — the first OK wins, so one dead relay never loses a post.
        </p>
      </div>
    </>
  );
}

function BlossomSettings() {
  const [url, setUrl] = useState(() => getBlossomServer() ?? '');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const save = () => {
    setErr(null); setMsg(null);
    try {
      setBlossomServer(url);
      setMsg(url.trim() ? 'Blossom server saved — private-board uploads now go there.' : 'Blossom server cleared — uploads fall back to third-party hosts.');
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <>
      <div className="rl-cat"><span>File hosting (Blossom)</span><HardDrive size={14} aria-hidden /></div>
      <div className="rl-panel" style={{ borderTop: 'none', borderRadius: '0 0 3px 3px' }}>
        <div className="rl-field">
          <label htmlFor="bl-url">Blossom server URL (BUD-01/02) — private-board uploads only</label>
          <input id="bl-url" className="rl-input rl-mono" placeholder="https://blossom.example.com"
                 value={url} onChange={e => setUrl(e.target.value)} />
        </div>
        <button className="rl-btn" onClick={save}>Save</button>
        {msg && <p className="rl-small" style={{ marginTop: 6 }}>{msg}</p>}
        {err && <p style={{ color: 'var(--rl-bad)', fontSize: 12 }}>{err}</p>}
        <p className="rl-small" style={{ marginTop: 8 }}>
          Upload auth events are signed with your per-board pseudonymous key, not your identity.
          Uploads stay disabled on public boards regardless of this setting.
        </p>
      </div>
    </>
  );
}

function ModeratorInbox() {
  const { signer, reports } = useStore();
  if (!signer || signer.pubkey !== MODERATOR_PUBKEY || reports.length === 0) return null;
  return (
    <>
      <div className="rl-cat"><span>Moderator inbox</span><Flag size={14} aria-hidden /></div>
      <div className="rl-panel" style={{ borderTop: 'none', borderRadius: '0 0 3px 3px' }}>
        {reports.slice().reverse().map((r, i) => (
          <div key={i} style={{ borderBottom: '1px solid var(--rl-border)', padding: '6px 0', fontSize: 12 }}>
            <div>
              <strong>{r.report.reason ?? 'no reason given'}</strong>
              {' '}<span className="rl-small">— reported by {shortNpub(r.from)} · {new Date(r.at).toLocaleString()}</span>
            </div>
            <div className="rl-mono rl-small">
              board {r.report.board.slice(0, 12)}… · post {r.report.target.slice(0, 12)}… · author {shortNpub(r.report.author)}
            </div>
            {r.report.subject && <div style={{ marginTop: 2 }}>Thread: <em>{r.report.subject}</em></div>}
            {r.report.excerpt && <div className="rl-small" style={{ marginTop: 2, opacity: 0.8 }}>{r.report.excerpt}</div>}
          </div>
        ))}
        <p className="rl-small" style={{ marginTop: 8 }}>
          Reports are session-only on this device — copy what you need per docs/moderation-response.md.
        </p>
      </div>
    </>
  );
}

function PlaintextKeyBanner() {
  const { keyIsPlaintext, encryptStoredKey } = useStore();
  const [pw, setPw] = useState('');
  const [err, setErr] = useState<string | null>(null);
  if (!keyIsPlaintext) return null;
  const go = () => {
    setErr(null);
    if (pw.length < 8) { setErr('At least 8 characters.'); return; }
    try { encryptStoredKey(pw); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };
  return (
    <div className="rl-panel" style={{ borderColor: 'var(--rl-bad)' }}>
      <strong>Your key is stored unencrypted.</strong>
      <span className="rl-small"> Set a passphrase to encrypt it on this device (NIP-49).</span>
      <div className="rl-actions" style={{ marginTop: 6 }}>
        <input className="rl-input" type="password" placeholder="New passphrase" value={pw}
               onChange={e => setPw(e.target.value)} style={{ maxWidth: 220 }} autoComplete="new-password" />
        <button className="rl-btn" onClick={go} disabled={pw.length < 8}>Encrypt key</button>
      </div>
      {err && <p style={{ color: 'var(--rl-bad)', fontSize: 12 }}>{err}</p>}
    </div>
  );
}

export default function BoardIndex() {  const { boards } = useStore();
  const list = useMemo(
    () => Object.values(boards).sort((a, b) => (a.meta?.name ?? '').localeCompare(b.meta?.name ?? '')),
    [boards],
  );

  return (
    <div>
      <PlaintextKeyBanner />
      <ModeratorInbox />
      <PublicBoards />
      <div className="rl-cat"><span>Boards</span><Lock size={13} aria-hidden /></div>
      <table className="rl-table">
        <thead>
          <tr>
            <th style={{ width: 34 }}></th>
            <th>Board</th>
            <th className="rl-num">Topics</th>
            <th className="rl-num">Posts</th>
            <th className="rl-last rl-hide-sm">Last post</th>
          </tr>
        </thead>
        <tbody>
          {list.length === 0 && (
            <tr className="rl-row-a">
              <td colSpan={5}>
                <div className="rl-empty">
                  No boards yet. Create one below, or join with an invite link.<br />
                  Boards you can’t decrypt are invisible here — by design.
                </div>
              </td>
            </tr>
          )}
          {list.map((m, i) => (
            <tr key={m.boardId} className={i % 2 ? 'rl-row-b' : 'rl-row-a'}>
              <td className="rl-icon"><MessagesSquare size={17} aria-hidden /></td>
              <td>
                <div className="rl-board-name">
                  <a href={`#/b/${m.boardId}`}>{m.meta?.name ?? 'Encrypted board…'}</a>
                </div>
                <div className="rl-desc">
                  {m.meta?.description ?? 'Fetching metadata…'}
                  {' '}<span className="rl-pill">epoch {m.currentEpoch}</span>
                  {m.role === 'admin' && <span className="rl-pill" style={{ marginLeft: 4 }}>you admin this</span>}
                </div>
              </td>
              <BoardStats m={m} />
            </tr>
          ))}
        </tbody>
      </table>

      <CreateBoard />
      <JoinBoard />
      <RelaySettings />
      <BlossomSettings />
    </div>
  );
}
